import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Chessboard } from 'react-chessboard';
import { Chess } from 'chess.js';
import { doc, onSnapshot, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';

const DEFAULT_ACCENT = '#749555';

const normalizeHex = (hex) => {
  if (!hex) return null;
  let normalized = hex.replace('#', '');
  if (normalized.length === 3) {
    normalized = normalized.split('').map(char => char + char).join('');
  }
  if (normalized.length !== 6) {
    return null;
  }
  return normalized;
};

const hexToRgba = (hex, alpha = 1) => {
  const normalized = normalizeHex(hex);
  if (!normalized) return `rgba(116, 149, 85, ${alpha})`;
  const num = parseInt(normalized, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};


const LiveChessGame = ({
  profileId,
  profileOwnerId,
  chessGameConfig,
  isOwnerView = false,
  accentColor = DEFAULT_ACCENT,
  profileUsername = '',
  profileDisplayName = '',
  showTurnBanner = false,
  isTransitioning = false,
  title = 'Play Chess With Me',
  headingColor,
  bodyColor,
  mutedColor,
  isDark,
}) => {
  const { currentUser } = useAuth();
  const [chess] = useState(new Chess());
  const [gamePosition, setGamePosition] = useState(chess.fen());
  const [gameHistory, setGameHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [gameStatus, setGameStatus] = useState('');
  const [lastMove, setLastMove] = useState(null);
  const lastRecordedResultSignatureRef = useRef(null);
  const isProcessingGameOverRef = useRef(false);
  const isProcessingMoveRef = useRef(false);
  const pendingMoveRef = useRef(null);
  const [isUsersTurn, setIsUsersTurn] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const safeAccent = accentColor || DEFAULT_ACCENT;

  // Determine if current user is the profile owner
  const isProfileOwner = currentUser?.uid === profileOwnerId ||
                        (profileId && localStorage.getItem('ownerAuth_' + profileId) === 'true');

  // Determine which color the current user can play
  const userCanPlay = isProfileOwner ? chessGameConfig?.ownerColor :
                     (chessGameConfig?.ownerColor === 'white' ? 'black' : 'white');

  // Reference to the game document
  const gameDocRef = useMemo(() => {
    if (!profileId) return null;
    return doc(db, 'playerProfiles', profileId, 'chessGames', 'current');
  }, [profileId]);

  // Initialize or load game from Firestore
  useEffect(() => {
    if (!profileId || !chessGameConfig?.enabled || !gameDocRef) {
      setIsLoading(false);
      return;
    }

    const initializeGame = async () => {
      try {
        const gameDoc = await getDoc(gameDocRef);

        if (gameDoc.exists()) {
          const gameData = gameDoc.data();

          if (gameData.pgn) {
            chess.loadPgn(gameData.pgn);
            setGamePosition(chess.fen());
            setGameHistory(gameData.moves || []);
            setLastMove(gameData.lastMove || null);
          } else if (gameData.fen) {
            chess.load(gameData.fen);
            setGamePosition(gameData.fen);
            setGameHistory(gameData.moves || []);
            setLastMove(gameData.lastMove || null);
          }
        } else {
          const initialGameData = {
            fen: chess.fen(),
            pgn: chess.pgn(),
            moves: [],
            ownerColor: chessGameConfig?.ownerColor || 'white',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          await setDoc(gameDocRef, initialGameData);
          lastRecordedResultSignatureRef.current = null;
        }
        setIsLoading(false);
      } catch (error) {
        console.error('Error initializing chess game:', error);
        setIsLoading(false);
      }
    };

    initializeGame();
  }, [profileId, chessGameConfig, gameDocRef, chess]);

  // Real-time listener for game updates
  useEffect(() => {
    if (!profileId || !chessGameConfig?.enabled || !gameDocRef) return;

    const unsubscribe = onSnapshot(gameDocRef, async (doc) => {
      if (doc.exists()) {
        const gameData = doc.data();

        if (isProcessingMoveRef.current && pendingMoveRef.current) {
          const pendingMove = pendingMoveRef.current;
          if (gameData.lastMove &&
              gameData.lastMove.san === pendingMove.san &&
              gameData.lastMove.from === pendingMove.from &&
              gameData.lastMove.to === pendingMove.to) {

            if (gameData.pgn) {
              chess.loadPgn(gameData.pgn);
            } else if (gameData.fen) {
              chess.load(gameData.fen);
            }

            setGamePosition(chess.fen());
            setGameHistory(gameData.moves || []);
            setLastMove(gameData.lastMove || null);

            isProcessingMoveRef.current = false;
            pendingMoveRef.current = null;

            return;
          }
        }

        if (isProcessingMoveRef.current) {
          return;
        }

        const isCorrupted = gameData.score && (
          (gameData.score.authWins || 0) > 1000 ||
          (gameData.score.publicWins || 0) > 1000 ||
          (gameData.score.draws || 0) > 1000
        );

        if (isCorrupted) {
          chess.reset();
          const resetGameData = {
            fen: chess.fen(),
            pgn: chess.pgn(),
            moves: [],
            lastMove: null,
            ownerColor: chessGameConfig?.ownerColor || 'white',
            gameOver: false,
            gameResult: null,
            resultSignature: null,
            score: null,
            updatedAt: new Date().toISOString(),
            resetAt: new Date().toISOString()
          };
          try {
            await setDoc(gameDocRef, resetGameData, { merge: true });
          } catch (error) {
            console.error('Error resetting corrupted game:', error);
          }
          setGamePosition(chess.fen());
          setGameHistory([]);
          setLastMove(null);
          lastRecordedResultSignatureRef.current = null;
          return;
        }

        if (gameData.pgn) {
          const currentPgn = chess.pgn();
          if (gameData.pgn !== currentPgn) {
            chess.loadPgn(gameData.pgn);
            setGamePosition(chess.fen());
            setGameHistory(gameData.moves || []);
            setLastMove(gameData.lastMove || null);
          } else {
            setLastMove(gameData.lastMove || null);
          }
        } else if (gameData.fen && gameData.fen !== gamePosition) {
          chess.load(gameData.fen);
          setGamePosition(gameData.fen);
          setGameHistory(gameData.moves || []);
          setLastMove(gameData.lastMove || null);
        }
      }
    });

    return () => unsubscribe();
  }, [profileId, chessGameConfig, gamePosition, gameDocRef, chess]);

  // Check for game over and reset board
  const checkGameOver = useCallback(async () => {
    if (isProcessingGameOverRef.current) return;
    if (!chess.isGameOver()) return;
    if (!gameDocRef) return;

    isProcessingGameOverRef.current = true;

    setTimeout(async () => {
      try {
        chess.reset();

        const resetGameData = {
          fen: chess.fen(),
          pgn: chess.pgn(),
          moves: [],
          lastMove: null,
          ownerColor: chessGameConfig?.ownerColor || 'white',
          gameOver: false,
          gameResult: null,
          score: null,
          updatedAt: new Date().toISOString(),
          resetAt: new Date().toISOString()
        };

        await setDoc(gameDocRef, resetGameData, { merge: true });

        setGamePosition(chess.fen());
        setGameHistory([]);
        setLastMove(null);
        lastRecordedResultSignatureRef.current = null;
      } catch (error) {
        console.error('Error resetting game:', error);
      } finally {
        isProcessingGameOverRef.current = false;
      }
    }, 3000);
  }, [chess, gameDocRef, chessGameConfig]);

  // Update game status based on position
  useEffect(() => {
    if (chess.isGameOver()) {
      if (chess.isCheckmate()) {
        const winner = chess.turn() === 'w' ? 'Black' : 'White';
        setGameStatus(`Checkmate! ${winner} wins!`);
      } else if (chess.isDraw()) {
        setGameStatus('Game is a draw');
      } else if (chess.isStalemate()) {
        setGameStatus('Stalemate');
      }
    } else if (chess.inCheck()) {
      const turn = chess.turn() === 'w' ? 'White' : 'Black';
      setGameStatus(`${turn} is in check`);
    } else {
      const turn = chess.turn() === 'w' ? 'White' : 'Black';
      setGameStatus(`${turn} to move`);
    }

    const ownerColor = chessGameConfig?.ownerColor || 'white';
    const userColor = isProfileOwner ? ownerColor : (ownerColor === 'white' ? 'black' : 'white');
    const turnColor = chess.turn() === 'w' ? 'white' : 'black';
    const gameOver = chess.isGameOver();
    const newIsUsersTurn = !gameOver && turnColor === userColor;

    setIsUsersTurn(newIsUsersTurn);
  }, [gamePosition, chessGameConfig?.ownerColor, isProfileOwner, chess]);

  // Handle piece drop
  const onDrop = useCallback(async (sourceSquare, targetSquare) => {
    isProcessingMoveRef.current = true;

    const currentTurn = chess.turn();
    const canMakeMove = (currentTurn === 'w' && userCanPlay === 'white') ||
                       (currentTurn === 'b' && userCanPlay === 'black');

    if (!canMakeMove) {
      isProcessingMoveRef.current = false;
      return false;
    }

    try {
      const originalFen = chess.fen();
      const originalHistory = [...gameHistory];

      const move = chess.move({
        from: sourceSquare,
        to: targetSquare,
        promotion: 'q'
      });

      if (move === null) {
        isProcessingMoveRef.current = false;
        return false;
      }

      const newPosition = chess.fen();
      const newHistory = [...gameHistory, move.san];
      const newLastMove = {
        from: sourceSquare,
        to: targetSquare,
        san: move.san,
        timestamp: new Date().toISOString()
      };

      setGamePosition(newPosition);
      setGameHistory(newHistory);
      setLastMove(newLastMove);

      const updateData = {
        fen: newPosition,
        pgn: chess.pgn(),
        moves: newHistory,
        lastMove: newLastMove,
        ownerColor: chessGameConfig?.ownerColor || 'white',
        gameOver: false,
        gameResult: null,
        resultSignature: null,
        updatedAt: new Date().toISOString()
      };

      try {
        pendingMoveRef.current = newLastMove;

        await setDoc(gameDocRef, updateData, { merge: true });
        lastRecordedResultSignatureRef.current = null;

        if (chess.isGameOver()) {
          checkGameOver();
        }

        setTimeout(() => {
          if (isProcessingMoveRef.current && pendingMoveRef.current) {
            isProcessingMoveRef.current = false;
            pendingMoveRef.current = null;
          }
        }, 5000);

        return true;
      } catch (firestoreError) {
        console.error('Firestore update failed:', firestoreError);

        chess.load(originalFen);
        setGamePosition(originalFen);
        setGameHistory(originalHistory);
        setLastMove(null);

        isProcessingMoveRef.current = false;
        pendingMoveRef.current = null;
        return false;
      }
    } catch (error) {
      console.error('Error making move:', error);
      isProcessingMoveRef.current = false;
      return false;
    }
  }, [chess, userCanPlay, gameHistory, chessGameConfig, gameDocRef, checkGameOver]);

  // Handle click-to-move
  const onSquareClick = useCallback((square) => {
    if (!selectedSquare) {
      // First click — select a piece if it belongs to the current player
      const piece = chess.get(square);
      if (!piece) return;
      const pieceColor = piece.color === 'w' ? 'white' : 'black';
      if (pieceColor !== userCanPlay) return;
      setSelectedSquare(square);
    } else {
      if (square === selectedSquare) {
        // Clicking the same square deselects
        setSelectedSquare(null);
        return;
      }
      // Check if clicking another own piece — reselect instead
      const piece = chess.get(square);
      if (piece) {
        const pieceColor = piece.color === 'w' ? 'white' : 'black';
        if (pieceColor === userCanPlay) {
          setSelectedSquare(square);
          return;
        }
      }
      // Second click — attempt the move
      onDrop(selectedSquare, square);
      setSelectedSquare(null);
    }
  }, [selectedSquare, chess, userCanPlay, onDrop]);

  // Get clean PGN without headers
  const getCleanPgn = useCallback(() => {
    const fullPgn = chess.pgn();
    if (!fullPgn) return '';

    const lines = fullPgn.split('\n');
    const moveLines = lines.filter(line =>
      !line.startsWith('[') &&
      !line.startsWith('*') &&
      line.trim() !== ''
    );

    return moveLines.join(' ').replace(/\s+/g, ' ').trim();
  }, [chess]);

  // Reset game (owner only)
  const resetGame = async () => {
    if (!isProfileOwner || !isOwnerView) return;

    try {
      chess.reset();
      lastRecordedResultSignatureRef.current = null;
      const resetGameData = {
        fen: chess.fen(),
        pgn: chess.pgn(),
        moves: [],
        lastMove: null,
        ownerColor: chessGameConfig?.ownerColor || 'white',
        gameOver: false,
        gameResult: null,
        resultSignature: null,
        score: null,
        updatedAt: new Date().toISOString(),
        resetAt: new Date().toISOString()
      };

      await setDoc(gameDocRef, resetGameData, { merge: true });
      setGamePosition(chess.fen());
      setGameHistory([]);
      setLastMove(null);
    } catch (error) {
      console.error('Error resetting game:', error);
    }
  };

  // Custom square styles for last move and selected square
  const customSquareStyles = {};
  if (lastMove) {
    const lastMoveHighlight = 'rgba(239, 68, 68, 0.4)';
    customSquareStyles[lastMove.from] = { backgroundColor: lastMoveHighlight };
    customSquareStyles[lastMove.to] = { backgroundColor: lastMoveHighlight };
  }
  if (selectedSquare) {
    customSquareStyles[selectedSquare] = {
      backgroundColor: hexToRgba(safeAccent, 0.6),
      borderRadius: '4px',
    };
  }

  // Determine board orientation
  const boardOrientation = isProfileOwner
    ? chessGameConfig?.ownerColor || 'white'
    : (chessGameConfig?.ownerColor === 'white' ? 'black' : 'white');

  const opponentLabel = profileDisplayName || profileUsername || 'the coach';

  // Early return if profileId is not available
  if (!profileId) {
    return (
      <div className="lcg-loading">
        <i className="fas fa-chess-board lcg-loading-icon"></i>
        <p>Loading profile...</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="lcg-loading">
        <i className="fas fa-chess-board lcg-loading-icon"></i>
        <p>Loading chess game...</p>
      </div>
    );
  }

  return (
    <div className="lcg-container">
      {/* Two-column layout */}
      <div className="lcg-layout">
        {/* LEFT COLUMN: Board */}
        <div className="lcg-left">
          {title && <h2 className="lcg-title lcg-title-mobile">{title}</h2>}

          <div className="lcg-board-wrapper">
            <Chessboard
              position={gamePosition}
              onPieceDrop={onDrop}
              onSquareClick={onSquareClick}
              boardOrientation={boardOrientation}
              customSquareStyles={customSquareStyles}
              areArrowsAllowed={false}
              arePremovesAllowed={false}
              customLightSquareStyle={{ backgroundColor: "#eeeed2" }}
              customDarkSquareStyle={{
                backgroundColor: chessGameConfig?.matchThemeSquares ? safeAccent : '#769656'
              }}
            />
          </div>
        </div>

        {/* RIGHT COLUMN: Title (desktop) + Game info panel */}
        <div className="lcg-right">
          {title && <h2 className="lcg-title lcg-title-desktop">{title}</h2>}
          <div className="lcg-info-card">
            <p className="lcg-matchup">{opponentLabel} vs. The World</p>
            <p className="lcg-play-as">Play as <strong className="capitalize">{userCanPlay}</strong></p>

            {/* Turn Status */}
            <div className="lcg-turn-status">
              <div
                className="lcg-status-dot"
                style={{ backgroundColor: isUsersTurn ? (isDark === false ? '#dc2626' : '#f87171') : safeAccent }}
              ></div>
              <span className={`lcg-turn-text ${isUsersTurn ? 'lcg-your-turn' : ''}`}>
                {gameStatus}
              </span>
            </div>
          </div>

          {/* PGN Display - clickable to copy */}
          <div
            className={`lcg-pgn${!getCleanPgn() ? ' lcg-pgn-empty-state' : ''}`}
            onClick={() => {
              if (!getCleanPgn()) return;
              navigator.clipboard.writeText(getCleanPgn());
              setShowCopied(true);
              setTimeout(() => setShowCopied(false), 1500);
            }}
            title={getCleanPgn() ? 'Click to copy PGN' : undefined}
          >
            <code className="lcg-pgn-moves">
              {getCleanPgn() ? (showCopied ? 'Copied!' : getCleanPgn()) : 'No moves yet'}
            </code>
          </div>

          {/* Reset Button (owner only) */}
          {isOwnerView && isProfileOwner && (
            <button
              onClick={resetGame}
              className="lcg-reset-btn"
              style={{
                color: safeAccent,
                borderColor: hexToRgba(safeAccent, 0.4)
              }}
            >
              <i className="fas fa-redo"></i>
              Reset Game
            </button>
          )}
        </div>
      </div>

      <style>{`
        .lcg-container {
          width: 100%;
        }

        .lcg-layout {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 32px;
          align-items: center;
        }

        /* LEFT COLUMN */
        .lcg-left {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
        }

        .lcg-title {
          font-size: 1.75rem;
          font-weight: 700;
          color: ${headingColor || '#ffffff'};
          margin: 0;
          text-align: center;
        }

        .lcg-title-mobile {
          display: none;
        }

        .lcg-title-desktop {
          display: block;
        }

        .lcg-board-wrapper {
          width: 100%;
          aspect-ratio: 1;
          max-width: 450px;
          overflow: hidden;
        }

        /* RIGHT COLUMN */
        .lcg-right {
          display: flex;
          flex-direction: column;
          gap: 12px;
          justify-content: center;
          height: 100%;
        }

        /* Info card */
        .lcg-info-card {
          background: ${isDark === false ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.04)'};
          border: 1px solid ${isDark === false ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'};
          border-radius: 14px;
          padding: 24px;
          text-align: center;
        }

        .lcg-matchup {
          font-size: 1.3rem;
          font-weight: 700;
          color: ${headingColor || '#e5e7eb'};
          margin: 0 0 4px 0;
        }

        .lcg-play-as {
          font-size: 0.9rem;
          color: ${mutedColor || '#9ca3af'};
          margin: 0 0 20px 0;
        }

        .lcg-play-as strong {
          color: ${headingColor || '#d1d5db'};
        }

        /* Turn Status */
        .lcg-turn-status {
          display: inline-flex;
          align-items: center;
          align-self: center;
          gap: 10px;
          padding: 10px 20px;
          background: ${isDark === false ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)'};
          border-radius: 10px;
          margin: 0;
        }

        .lcg-status-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          flex-shrink: 0;
          animation: lcg-pulse 2s ease-in-out infinite;
        }

        @keyframes lcg-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .lcg-turn-text {
          font-size: 1rem;
          font-weight: 600;
          color: ${headingColor || '#e5e7eb'};
        }

        .lcg-turn-text.lcg-your-turn {
          color: ${isDark === false ? '#dc2626' : '#f87171'};
        }

        .capitalize {
          text-transform: capitalize;
        }

        /* PGN */
        .lcg-pgn {
          padding: 14px 18px;
          background: ${isDark === false ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.04)'};
          border: 1px solid ${isDark === false ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'};
          border-radius: 14px;
          cursor: pointer;
          transition: background 0.15s ease;
          text-align: center;
          min-height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .lcg-pgn:hover {
          background: ${isDark === false ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.07)'};
        }

        .lcg-pgn-moves {
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 0.85rem;
          color: ${mutedColor || '#94a3b8'};
          line-height: 1.6;
          word-break: break-word;
        }

        .lcg-pgn-empty-state {
          cursor: default;
        }

        .lcg-pgn-empty-state .lcg-pgn-moves {
          font-style: italic;
          color: ${mutedColor || '#4b5563'};
        }

        .lcg-pgn-empty-state:hover {
          background: ${isDark === false ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.04)'};
        }

        /* Reset Button */
        .lcg-reset-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px 16px;
          background: transparent;
          border: 1px solid;
          border-radius: 10px;
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .lcg-reset-btn:hover {
          background: ${hexToRgba(safeAccent, 0.1)};
        }

        /* Loading State */
        .lcg-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 24px;
          color: #94a3b8;
        }

        .lcg-loading-icon {
          font-size: 2.5rem;
          color: #64748b;
          margin-bottom: 16px;
          animation: lcg-pulse 1.5s ease-in-out infinite;
        }

        /* MOBILE: Stack vertically */
        @media (max-width: 900px) {
          .lcg-layout {
            grid-template-columns: 1fr;
            gap: 24px;
          }

          .lcg-title {
            text-align: center;
          }

          .lcg-title-mobile {
            display: block;
          }

          .lcg-title-desktop {
            display: none;
          }

          .lcg-board-wrapper {
            max-width: 400px;
          }
        }

        @media (max-width: 480px) {
          .lcg-title {
            font-size: 1.5rem;
          }

          .lcg-board-wrapper {
            max-width: calc(100vw - 48px);
          }

          .lcg-status-card,
          .lcg-rules,
          .lcg-pgn {
            padding: 14px;
          }

          .lcg-rules-list li {
            font-size: 0.85rem;
          }
        }
      `}</style>
    </div>
  );
};

export default LiveChessGame;
